import { useEffect, useState } from "react";
import { LuClock } from "react-icons/lu";

/**
 * Live countdown to the next quota reset. Cloudflare resets daily
 * counters at midnight UTC and monthly counters at the first of the
 * next UTC month. Re-renders every second so the operator can see the
 * window closing.
 */
export function ResetCountdown({
  period,
  className = "",
  compact = false,
}: {
  period: "day" | "month" | "total";
  className?: string;
  compact?: boolean;
}) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (period === "total") return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [period]);

  if (period === "total") return null;

  const target = nextResetUtc(period, now);
  const remainingMs = Math.max(0, target - now);
  const { d, h, m, s } = breakDown(remainingMs);
  const labelTarget = period === "day" ? "midnight UTC" : "month rollover";

  if (compact) {
    return (
      <span
        className={`inline-flex items-center gap-1 text-[11px] text-[var(--color-text-subtle)] tabular-nums font-mono ${className}`}
        title={`Resets at ${new Date(target).toUTCString()}`}
      >
        <LuClock className="w-3 h-3" />
        {d > 0 ? `${d}d ` : ""}
        {String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
      </span>
    );
  }

  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] ${className}`}
      title={`Resets at ${new Date(target).toUTCString()}`}
    >
      <LuClock className="w-3.5 h-3.5 text-[var(--color-accent-light)]" />
      <span className="text-[11px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)]">
        {period === "day" ? "Daily reset" : "Monthly reset"}
      </span>
      <span className="text-sm font-mono text-[var(--color-text)] tabular-nums">
        {d > 0 ? `${d}d ` : ""}
        {String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
      </span>
      <span className="text-[10px] text-[var(--color-text-subtle)]">
        until {labelTarget}
      </span>
    </div>
  );
}

function nextResetUtc(period: "day" | "month", nowMs: number): number {
  const now = new Date(nowMs);
  if (period === "day") {
    return Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() + 1,
      0,
      0,
      0,
    );
  }
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 0, 0, 0);
}

function breakDown(ms: number) {
  const total = Math.floor(ms / 1000);
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return { d, h, m, s };
}
